/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import queryString from 'query-string';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { PreferenceService } from '@bingads-webui/preference-service';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { getCampaignType } from '@bingads-webui-campaign-react/with-campaign-type';
import state from '@bingads-webui-component-legacy/state/index';
import { withAppPerfMarker } from '../contexts/app-perfmarker-context';
import { isDFMEnabledForPath } from './is-dfm-enabled-for-path';

const DFMQueryKeys = [
  'uid',
  'cid',
  'aid',
  'campaignId',
  'adGroupId',
  'adId',
  'filterAlias',
  'filterArguments',
  'filterExpressions',
];

const getDFMQueryId = ({ pathname, search }) => {
  const queryValues = queryString.parse(search);
  const queryKey = DFMQueryKeys.map(key => `${key}:${queryValues[key]}`).join(',');
  return `${pathname}?${queryKey}`;
};

export const DFMContext = React.createContext();

export function withDFMProvider(Component) {
  class DFMProvider extends React.PureComponent {
    constructor(props) {
      super(props);
      this.state = {
        dfmPromise: Promise.resolve(),
      };
    }

    componentDidMount() {
      const {
        history, serverContext, appPerfMarker,
      } = this.props;

      const dfmPerfMarker = appPerfMarker.createChild('Data_Fetch_Manager_V2');

      let { pathname: prePathname, search: preSearch } = history.location;
      // the history change event is always triggered before the react-router navigation logic and page mount logic
      // so it's the best place for DFM call
      history.listen((location) => {
        const { pathname, search } = location;
        // If the route and query keys which dfm depends has not changed, do not update dfmPromise
        const preDFMQueryId = getDFMQueryId({ pathname: prePathname, search: preSearch });
        const curDFMQueryId = getDFMQueryId({ pathname, search });
        if (preDFMQueryId === curDFMQueryId) {
          return;
        }
        prePathname = pathname;
        preSearch = search;

        const params = new URLSearchParams(preSearch);
        const campaignType = getCampaignType(Number(params.get('campaignId')));

        // if not a allowed pathname, skip dfm fetch
        if (!isDFMEnabledForPath(pathname, serverContext, campaignType)) {
          const dfmPromise = Promise.resolve();
          dfmPromise.id = curDFMQueryId;
          this.setState({
            dfmPromise,
          });
          return;
        }

        if (state && state.set) {
          // reset page number before loading DFM.
          state.set('pgridPageNumber', {});
        }

        const dfmPromise = import(/* webpackMode: 'eager' */'./data-fetch-manager')
          .then(({ fetchData }) => fetchData({
            serverContext,
            preferenceService: PreferenceService,
            loadingPath: pathname,
            dfmPerfMarker,
            campaignType,
          }));
        dfmPromise.id = curDFMQueryId;
        this.setState({
          dfmPromise,
        });
      });
    }

    render() {
      return (
        <DFMContext.Provider value={this.state.dfmPromise}>
          <Component {...this.props} />
        </DFMContext.Provider>
      );
    }
  }

  DFMProvider.propTypes = {
    history: PropTypes.shape({
      listen: PropTypes.func.isRequired,
    }).isRequired,
    serverContext: PropTypes.shape({
      PageTrackingId: PropTypes.string.isRequired,
      CurrentLocale: PropTypes.string.isRequired,
      CurrentAccount: PropTypes.shape({
        AccountNumber: PropTypes.string,
      }),
      ServicesEndpoints: PropTypes.shape({
        ClientCenterUIAPI: PropTypes.string.isRequired,
      }),
    }).isRequired,
    appPerfMarker: perfMarkerPropTypes.isRequired,
    permissions: PropTypes.shape({
      IsDataFetchManagerV2Enabled: PropTypes.bool.isRequired,
    }).isRequired,
  };

  return withPermissions(withAppPerfMarker(withServerContext(withRouter(DFMProvider))));
}

/**
 * Use Context.Consumer instead of Class.contextType
 * Because there are some PureComponents between DFMProvider and DFMReady
 * React will not trigger DFMReady rendering if we use Class.contextType
 * @param {React.Component} Component component
 * @returns {React.Component} component with dfmPromise props
 */
function withDFMConsumer(Component) {
  const DFMConsumer = props => (
    <DFMContext.Consumer>
      {dfmPromise => <Component {...props} dfmPromise={dfmPromise} />}
    </DFMContext.Consumer>
  );
  return DFMConsumer;
}

export function withDFMReady(Component) {
  class DFMReady extends React.PureComponent {
    constructor(props) {
      super(props);
      this.dfmTriggered = false;
      this.preDFMPromise = null;
    }

    updateDFMTriggered() {
      const { dfmPromise, location } = this.props;
      const expectedDFMQueryId = getDFMQueryId(location);
      const isFirstRender = !dfmPromise.id;
      const isNewDFMPromise = dfmPromise !== this.preDFMPromise;
      const isExpectedDFMPromise = dfmPromise.id === expectedDFMQueryId;
      if (isNewDFMPromise && (isFirstRender || isExpectedDFMPromise)) {
        this.dfmTriggered = false;
        this.preDFMPromise = dfmPromise;
        dfmPromise.finally(() => {
          this.dfmTriggered = true;
          this.forceUpdate();
        });
      }
    }

    render() {
      /**
       * Update this.dfmTriggered before each rendering instead of in componentDidMount
       * Because updating the search without updating pathname also requires triggering remount
       */
      this.updateDFMTriggered();
      return this.dfmTriggered ? <Component {...this.props} /> : null;
    }
  }

  DFMReady.propTypes = {
    dfmPromise: PropTypes.shape({
      id: PropTypes.string,
      then: PropTypes.func.isRequired,
      catch: PropTypes.func.isRequired,
      finally: PropTypes.func.isRequired,
    }).isRequired,
  };

  return withPermissions(withDFMConsumer(DFMReady));
}
